import {FC, Fragment, useCallback, useEffect, useMemo, useState} from 'react'
import {Link} from 'react-router-dom'
import {getReferrers, ReferrersResponse, ReferrerRow} from './_requests'

const dateLabel = (iso: string) => {
  if (!iso) return ''
  const d = new Date(iso)
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
}

const timeAgo = (iso: string) => {
  if (!iso) return ''
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (days <= 0) return 'היום'
  if (days === 1) return 'אתמול'
  if (days < 30) return `לפני ${days} ימים`
  const months = Math.floor(days / 30)
  return months < 12 ? `לפני ${months} חודשים` : `לפני ${Math.floor(months / 12)} שנים`
}

const card = (bg: string): React.CSSProperties => ({
  background: bg,
  borderRadius: 12,
  padding: '18px 20px',
  color: '#fff',
  flex: '1 1 180px',
  minWidth: 180,
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
})

const ReferrersPage: FC = () => {
  const [data, setData] = useState<ReferrersResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [sort, setSort] = useState<'latest' | 'count'>('latest')
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [search, setSearch] = useState('')

  const load = useCallback(async (s: 'latest' | 'count') => {
    setLoading(true)
    setError('')
    try {
      const {data} = await getReferrers(s)
      setData(data)
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'שגיאה בטעינת הנתונים')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(sort)
  }, [load, sort])

  const toggle = (id: string) => setExpanded((s) => ({...s, [id]: !s[id]}))

  const rows = useMemo(() => {
    if (!data) return []
    const q = search.trim().toLowerCase()
    if (!q) return data.referrers
    return data.referrers.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.phone.includes(q) ||
        r.affiliate_code.toLowerCase().includes(q)
    )
  }, [data, search])

  return (
    <div style={{padding: 24, direction: 'rtl'}}>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">🤝 מי הביא מי — הפניות</h2>
          <div className="text-muted fs-7">חשבונות שהביאו חשבונות אחרים (חבר מביא חבר / שותפים)</div>
        </div>
        <button className="btn btn-sm btn-light" onClick={() => load(sort)} disabled={loading}>
          <i className="bi bi-arrow-clockwise me-1" /> רענן
        </button>
      </div>

      {/* Summary cards */}
      {data && (
        <div className="d-flex gap-3 flex-wrap mb-4">
          <div style={card('linear-gradient(135deg,#7c3aed,#5b21b6)')}>
            <div className="fs-2 fw-bold">{data.total_referrers.toLocaleString('he-IL')}</div>
            <div className="fs-7 opacity-75">חשבונות שהפנו</div>
          </div>
          <div style={card('linear-gradient(135deg,#2b70fa,#0d65d9)')}>
            <div className="fs-2 fw-bold">{data.total_referred.toLocaleString('he-IL')}</div>
            <div className="fs-7 opacity-75">סה"כ חשבונות שהופנו</div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <div className="btn-group btn-group-sm" role="group">
          <button
            className={`btn ${sort === 'latest' ? 'btn-primary' : 'btn-light'}`}
            onClick={() => setSort('latest')}
          >
            אחרונים
          </button>
          <button
            className={`btn ${sort === 'count' ? 'btn-primary' : 'btn-light'}`}
            onClick={() => setSort('count')}
          >
            הכי הרבה הפניות
          </button>
        </div>
        <input
          className="form-control form-control-sm"
          style={{maxWidth: 280}}
          placeholder="חיפוש מפנה לפי שם / מייל / טלפון / קוד..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" />
        </div>
      )}

      {!loading && data && (
        <div className="table-responsive">
          <table className="table table-row-bordered align-middle gy-2">
            <thead>
              <tr className="fw-bold text-muted fs-7">
                <th style={{width: 30}}></th>
                <th>מפנה</th>
                <th className="text-center">הפניות</th>
                <th>הפנייה אחרונה</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: ReferrerRow) => (
                <Fragment key={r._id}>
                  <tr style={{cursor: 'pointer'}} onClick={() => toggle(r._id)}>
                    <td className="text-center">
                      <i className={`bi bi-chevron-${expanded[r._id] ? 'down' : 'left'} text-muted`} />
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div className="fw-bold">{r.name}</div>
                        {r.is_affiliate ? (
                          <span className="badge badge-light-primary fs-8">שותף{r.affiliate_code ? ` · ${r.affiliate_code}` : ''}</span>
                        ) : (
                          <span className="badge badge-light-success fs-8">לקוח</span>
                        )}
                      </div>
                      <div className="text-muted fs-8">
                        {r.email}
                        {r.phone ? ` · ${r.phone}` : ''}
                      </div>
                    </td>
                    <td className="text-center">
                      <span className="badge badge-primary fs-7">{r.count}</span>
                    </td>
                    <td>
                      <div className="fw-semibold fs-7">{dateLabel(r.latest)}</div>
                      <div className="text-muted fs-8">{timeAgo(r.latest)}</div>
                    </td>
                    <td className="text-end" onClick={(e) => e.stopPropagation()}>
                      <Link to={`/admin/accounts/${r._id}`} className="btn btn-sm btn-light-primary py-1">
                        פרופיל
                      </Link>
                    </td>
                  </tr>
                  {expanded[r._id] && (
                    <tr>
                      <td colSpan={5} style={{background: '#faf9ff', padding: '8px 16px'}}>
                        <div className="fw-bold fs-8 text-muted mb-2">
                          {r.count > r.referred.length
                            ? `${r.referred.length} מתוך ${r.count} האחרונים שהופנו:`
                            : `החשבונות שהופנו (${r.referred.length}):`}
                        </div>
                        <div className="table-responsive">
                          <table className="table table-sm align-middle mb-0">
                            <tbody>
                              {r.referred.map((c) => (
                                <tr key={c._id}>
                                  <td>
                                    <Link to={`/admin/accounts/${c._id}`} className="fw-semibold text-dark">
                                      {c.name || '(ללא שם)'}
                                    </Link>
                                  </td>
                                  <td className="text-muted fs-8">{c.email}</td>
                                  <td className="text-muted fs-8">{c.phone}</td>
                                  <td className="text-muted fs-8">נרשם {dateLabel(c.created_at)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted p-4">
                    לא נמצאו מפנים
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default ReferrersPage
